/**
 * stem-rewriter — turn OpenTDB prompts into Jeopardy-style "this/these" stems.
 *
 * Strategy (rule-based, no LLM):
 *
 * 1. If the prompt starts with an interrogative ("Who", "What", "Which",
 *    "Where", "When", "Whose", "How many"), rewrite to a declarative using
 *    a demonstrative ("This", "These").
 *      "What is the capital of France?" → "This is the capital of France."
 *      "Who painted the Mona Lisa?" → "This person painted the Mona Lisa."
 *      "Which planet is closest to the sun?" → "This planet is closest to the sun."
 *      "When did WWII end?" → "World War II ended in this year."
 *      "Where is the Eiffel Tower located?" → "The Eiffel Tower is located in this city."
 *      "Whose tomb is in the Taj Mahal?" → "This person's tomb is in the Taj Mahal."
 *
 * 2. If the prompt is a declarative with a blank ("___" or "_____") or
 *    "...":
 *      "The capital of France is ___" → "This is the capital of France."
 *
 * 3. If none of the above apply, fall back to: keep the prompt verbatim
 *    with a "This is the answer to:" preamble. Coarse but reliable.
 *
 * The answer is wrapped into a "What is X?" / "Who is X?" canonical form
 * for the matcher and display. We pick "Who" vs "What" by whether the
 * answer is a proper noun referring to a person. The matcher accepts
 * either prefix anyway, so this is purely cosmetic.
 *
 * We DROP questions that can't be rewritten gracefully — these usually
 * have prompts that depend on multiple-choice context (e.g. "Which of
 * these is NOT a primary color?") and don't translate to Jeopardy form.
 */

export interface RewriteResult {
  stem: string;
  /** Display form: "What is Paris?" or "Who is Aretha Franklin?" */
  canonicalAnswer: string;
  /** Bare form for matching / fall-back display: "Paris", "Aretha Franklin". */
  bareAnswer: string;
  /** Additional matcher-acceptable forms. */
  acceptableAnswers: string[];
}

/**
 * Rewrite a prompt+answer into a Jeopardy clue. Returns null if the rewriter
 * can't produce a clean Jeopardy form for this input.
 */
export function rewriteToClue(
  prompt: string,
  answer: string,
): RewriteResult | null {
  const trimmedPrompt = prompt.trim();
  const trimmedAnswer = answer.trim();
  if (!trimmedPrompt || !trimmedAnswer) return null;

  // Hard rejects — these prompts don't translate cleanly.
  if (isHardReject(trimmedPrompt)) return null;

  const stem = buildStem(trimmedPrompt);
  if (!stem) return null;

  // Quality check on the stem — should look declarative, end in a period.
  if (!/[.?!]$/.test(stem)) return null;
  if (stem.length < 12) return null;

  const isPersonish = looksLikePerson(trimmedAnswer);
  const prefix = isPersonish ? 'Who is' : 'What is';
  const canonical = `${prefix} ${trimmedAnswer}?`;

  return {
    stem,
    canonicalAnswer: canonical,
    bareAnswer: trimmedAnswer,
    acceptableAnswers: alternatesFor(trimmedAnswer),
  };
}

// ---------------------------------------------------------------------------
// Stem construction
// ---------------------------------------------------------------------------

/**
 * Build the declarative stem from the question. Returns null if no rule
 * matches well.
 */
function buildStem(prompt: string): string | null {
  // Strip trailing punctuation while keeping the literal text.
  const body = prompt.replace(/[?!.\s]+$/, '');

  // Shared guard: a stem that would strand a bare preposition at the end
  // reads as broken English ("This is real haggis made of." / "This of
  // these companies does NOT manufacture automobiles."). Drop the clue
  // rather than ship it. Rules 1a, 1c, and 4b apply this check before
  // emitting their fallback "This X" form.
  const endsWithStrandedPrep = (s: string): boolean =>
    /\b(for|to|with|about|on|in|at|from|over|under|by|of|into|onto|out)$/i.test(
      s,
    );

  // Rule 1a: "What is/are <X>" → "This is <X>." / "These are <X>."
  let m = body.match(/^What\s+(is|was)\s+(.+)$/i);
  if (m) {
    const verb = m[1]!.toLowerCase();
    const rest = m[2]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    return `This ${verb} ${rest}.`;
  }
  m = body.match(/^What\s+(are|were)\s+(.+)$/i);
  if (m) {
    const verb = m[1]!.toLowerCase();
    const rest = m[2]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    return `These ${verb} ${rest}.`;
  }

  // Rule 1b: "What do/does <subject> <verb> <X>" — only handles the clean
  // present-tense form where the trailing word looks like a direct object.
  //
  //   "What do sailors call the left side of a boat?" →
  //     "Sailors call the left side of a boat this."
  //   "What does an octopus eat?" → "An octopus eat this."
  //
  // We DON'T handle "What did X verb Y" — past-tense rewrites need verb
  // conjugation we don't have (e.g., "stand for" → "stood for") and the
  // alternative ("...for X did this") strands the preposition. The "did"
  // family falls through to the generic Rule 1c "This X" form, which is
  // less elegant but doesn't strand prepositions.
  //
  // Also skip when the prompt ends with a preposition that would strand at
  // the end ("for", "to", "with", "about", etc.) — those don't read as
  // "...X this." cleanly.
  m = body.match(/^What\s+(do|does)\s+(.+)$/i);
  if (m) {
    const rest = m[2]!.trim();
    if (endsWithStrandedPrep(rest)) {
      // Drop the clue rather than fall through to a worse rule that would
      // emit "This do the letters of KFC stand for." (Rule 1c used to take
      // over here; it produced grammatically broken stems for the audience.)
      return null;
    }
    return `${capitalize(rest)} this.`;
  }

  // Rule 1c: "What <verb> ..." — generic catch-all. We only use this when
  // the prompt starts with a noun-shaped rest ("What instrument is used to
  // measure X" → "This instrument is used to measure X.") AND the rest
  // doesn't strand a preposition.
  m = body.match(/^What\s+(.+)$/i);
  if (m) {
    const rest = m[1]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    // Drop forms starting with do/does/did + subject — they emit "This does
    // X verb." which reads as broken English.
    if (/^(do|does|did)\s+/i.test(rest)) return null;
    // Drop "noun does/do/did subject" forms ("What direction does the
    // Statue of Liberty face?" → "This direction does the Statue of Liberty
    // face." — ungrammatical "this X does Y verb" structure).
    if (/^\S+\s+(do|does|did)\s+/i.test(rest)) return null;
    return `This ${rest}.`;
  }

  // Rule 2: "Who is/was <X>" → "This person <verb> <X>"
  m = body.match(/^Who\s+(is|was|are|were)\s+(.+)$/i);
  if (m) {
    const verb = m[1]!.toLowerCase();
    const rest = m[2]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    return `This person ${verb} ${rest}.`;
  }
  // "Who <verb> X" → "This person <verb> X"
  m = body.match(/^Who\s+(.+)$/i);
  if (m) {
    const rest = m[1]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    // Drop "Who did X verb Y" forms — past-tense conjugation problem same
    // as Rule 6.
    if (/^did\s+/i.test(rest)) return null;
    return `This person ${rest}.`;
  }

  // Rule 3: "Whose <noun> ..." → "This person's <noun> ..."
  m = body.match(/^Whose\s+(.+)$/i);
  if (m) {
    const rest = m[1]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    return `This person's ${rest}.`;
  }

  // Rule 4a: "Which of the(se) following <X> <verb> Y" / "Which of these <X> ..."
  //          → "This is one of these <X>: <verb> Y" doesn't read well; instead
  //          we reword to "One of these <X> <verb> Y. This is the one."
  //
  // Practical handling:
  //   "Which of the following Soviet revolutionaries was exiled from the USSR…"
  //   → "Of these Soviet revolutionaries, this one was exiled from the USSR…"
  //
  //   "Which of the following are primary colors" (no verb after the noun) →
  //   doesn't parse, drop the clue rather than ship something awkward.
  m = body.match(/^Which\s+of\s+(?:the\s+)?(?:these|the\s+following)\s+(.+)$/i);
  if (m) {
    const rest = m[1]!.trim();
    // Try to split "<noun phrase> <verb> <rest>". The simplest heuristic:
    // find the first occurrence of a common copula / past-tense verb cue.
    const verbMatch = rest.match(
      /^(.+?)\s+(is|are|was|were|did|do|does|has|have|had|can|will|would|could)\s+(.+)$/i,
    );
    if (verbMatch) {
      const nounPhrase = verbMatch[1]!.trim();
      const verb = verbMatch[2]!.toLowerCase();
      const tail = verbMatch[3]!.trim();
      // Don't emit if the tail strands a preposition — the constructed stem
      // would read as broken English.
      if (endsWithStrandedPrep(tail)) return null;
      // "Of these <noun>, this one <verb> <tail>."
      return `Of these ${nounPhrase}, this one ${verb} ${tail}.`;
    }
    // No clear verb split — the clue's only context was the multiple-choice
    // list. Drop it.
    return null;
  }

  // Rule 4b: "Which <noun> ..." → "This <noun> ..."
  // Drop any rest with a stranded preposition. Drop "Which X does Y verb"
  // forms — "This X does Y verb." reads as broken English ("This country
  // does Austria not border."). Drop "Which X did Y verb" for the same
  // reason as Rule 6 (we can't past-tense reliably).
  m = body.match(/^Which\s+(.+)$/i);
  if (m) {
    const rest = m[1]!.trim();
    if (endsWithStrandedPrep(rest)) return null;
    // "noun + does/do/did + ..." pattern: drop the clue.
    if (/^\S+\s+(do|does|did)\s+/i.test(rest)) return null;
    return `This ${rest}.`;
  }

  // Rule 5: "Where is/was/are/were <X>" → "<X> is/are located here."
  // Drop the clue when the rest contains a stranded participle ("explorer
  // Ferdinand Magellan killed in 1521" reads as broken English when wedged
  // into "<rest> was located here.").
  m = body.match(/^Where\s+(is|was|are|were)\s+(.+)$/i);
  if (m) {
    const verb = m[1]!.toLowerCase();
    // Strip a trailing "located"/"situated" to avoid double-up.
    const rest = m[2]!.replace(/\s+(located|situated)\s*$/i, '').trim();
    // Stranded participle check: "<noun> <past-participle> in <year>" — if a
    // mid-sentence participle like "killed" / "born" / "buried" / "founded"
    // appears, the original is asking *where the event happened*, not where
    // a thing is located. Our "X was located here" form reads as
    // ungrammatical.
    if (/\b(killed|born|buried|founded|signed|crowned|assassinated|married|elected|crowned|exiled)\b/i.test(rest)) {
      return null;
    }
    return `${capitalize(rest)} ${verb} located here.`;
  }
  // "Where did X happen" / "Where do X live" → "X happened/lived here."
  // Same conjugation problem as Rule 6 — we can't reliably past-tense the
  // bare verb in "Where did X verb Y" without a conjugator. Drop these.
  m = body.match(/^Where\s+(did|do|does)\s+(.+)$/i);
  if (m) {
    const verbForm = m[1]!.toLowerCase();
    const rest = m[2]!.trim();
    if (verbForm === 'did') {
      // Try the same trailing-helper trick as Rule 6.
      const happenStripped = rest.replace(
        /\s+(occur|occurred|happen|happened|take place|took place)\s*$/i,
        '',
      );
      if (happenStripped !== rest && happenStripped.trim().length > 0) {
        return `${capitalize(happenStripped.trim())} happened in this place.`;
      }
      return null;
    }
    // "Where do X live" / "Where does X live" — present-tense, only emit
    // when not preposition-stranded.
    if (endsWithStrandedPrep(rest)) return null;
    return `${capitalize(rest)} happens in this place.`;
  }

  // Rule 6: "When did <X>" — the most common form. The subject can be a noun
  // phrase ending in a bare verb ("Pong release"), or a verb-phrase form
  // ("World War II end"). We want "<X-with-past-tense-verb> in this year."
  //
  // We DON'T have a conjugator and the bare-infinitive verb left behind in
  // the rest reads as broken English ("Vesuvius destroy the city of
  // Pompeii happened in this year."). We only emit a stem when:
  //   (a) the rest ends with "happen / occur / take place" — strip the
  //       trailing verb and use the prefix as the subject phrase, OR
  //   (b) the verb is one we can past-tense correctly (a small whitelist of
  //       irregular forms and an "-ed" rule for clearly regular bare-verb
  //       tails).
  // Everything else drops — better to lose a few clues than ship ungrammatical
  // stems to the senior-audience.
  m = body.match(/^When\s+did\s+(.+)$/i);
  if (m) {
    const raw = m[1]!.trim();
    // Pattern (a): trailing "happen/occur/take place".
    const happenStripped = raw.replace(
      /\s+(occur|occurred|happen|happened|take place|took place)\s*$/i,
      '',
    );
    if (happenStripped !== raw) {
      const subject = happenStripped.trim();
      if (subject.length > 0) {
        return `${capitalize(subject)} happened in this year.`;
      }
    }
    // Pattern (b): handle the clean "Subject Verb" shape — exactly two
    // tokens, the second being a verb we can past-tense from the irregular
    // table. "Pong release" → "Pong released in this year." We DON'T try to
    // walk a longer string looking for the verb — "Vesuvius destroy the
    // city of Pompeii" would past-tense "Pompeii" (the last token) and
    // produce "Pompeiied" — broken. Better to drop these.
    const tokens = raw.split(/\s+/);
    if (tokens.length === 2) {
      const verb = tokens[1]!;
      const pasted = pastTenseFor(verb);
      if (pasted) {
        return `${capitalize(tokens[0]!)} ${pasted} in this year.`;
      }
    }
    // Pattern (c): "Subject + irregular-table-verb + ..." shape — verb is
    // the second token AND it's in our irregular table. Rest is appended as
    // the object.
    if (tokens.length >= 3) {
      const candidate = tokens[1]!.toLowerCase().replace(/[?.!,]+$/, '');
      if (IRREGULAR_PAST[candidate]) {
        const subject = tokens[0]!;
        const tail = tokens.slice(2).join(' ');
        return `${capitalize(subject)} ${IRREGULAR_PAST[candidate]} ${tail} in this year.`;
      }
    }
    return null;
  }
  // "When was/were/is/are <X>" — depends on shape:
  //
  //   "When was <subject> <past-participle>?" → "<Subject> was <participle> in this year."
  //     "When was penicillin discovered?" → "Penicillin was discovered in this year."
  //
  //   "When was <event>?" (no participle) → "<Event> was in this year."
  //     "When was the Battle of Hastings?" → "The Battle of Hastings was in this year."
  //
  // We detect the participle form by looking for a trailing single word after
  // a noun phrase: "<noun-phrase> <past-participle>". Common participles end in
  // "-ed", "-en", "-own", "-orn", etc.
  m = body.match(/^When\s+(was|were|is|are|will)\s+(.+)$/i);
  if (m) {
    const verb = m[1]!.toLowerCase();
    const rest = m[2]!
      .replace(/\s+(occur|happen|take place|happened|occurred)\s*$/i, '')
      .trim();
    // Check for trailing participle.
    const partMatch = rest.match(
      /^(.+)\s+(\w+(?:ed|en|own|orn|orn|olen|aught|ought|rought|ung|ame|one|orn|orne|orne))$/i,
    );
    if (partMatch) {
      const subject = partMatch[1]!.trim();
      const participle = partMatch[2]!.toLowerCase();
      return `${capitalize(subject)} ${verb} ${participle} in this year.`;
    }
    return `${capitalize(rest)} ${verb} in this year.`;
  }

  // Rule 7: "How many <X>" → "This is the number of <X>."
  //
  // Strip a redundant copula/auxiliary tail that creates double-subject
  // awkwardness:
  //   "How many planets are there in the Solar System?" →
  //     before: "This is the number of planets are there in the Solar System."
  //     after:  "This is the number of planets in the Solar System."
  //   "How many states start with the letter K?" →
  //     before: "This is the number of states start with the letter K."
  //     after:  "This is the number of states that start with the letter K."
  m = body.match(/^How\s+many\s+(.+)$/i);
  if (m) {
    let rest = m[1]!;
    // Drop "are there" / "are in <X>" stranded auxiliaries.
    rest = rest.replace(/\s+are\s+there\b/i, '');
    rest = rest.replace(/^are\s+/i, '');
    // "X are in Y" → "X in Y" (drop the auxiliary copula).
    rest = rest.replace(/\s+are\s+(in|on|at|of)\s+/i, ' $1 ');
    // "X does Y have" → "X Y has". Handle "does <subject> have/contain/etc."
    // by moving the verb to the end.
    const doesMatch = rest.match(
      /^(.+?)\s+does\s+(.+?)\s+(have|contain|include|cost|weigh|measure)\s*(.*)$/i,
    );
    if (doesMatch) {
      const subject = doesMatch[2]!.trim();
      const verb = doesMatch[3]!.toLowerCase();
      const tail = (doesMatch[4] ?? '').trim();
      // Pluralize "have" → "has" only when subject is third-person singular;
      // we don't reliably know that, so leave the verb as-is.
      const noun = doesMatch[1]!.trim();
      rest = `${noun} ${subject} ${verb}${tail ? ' ' + tail : ''}`;
    }
    // If the remainder starts with a bare verb (no subject), prefix with "that".
    const startsWithBareVerb = /^(start|begin|end|stop|win|lose|have|has|had|contain|include|live|fly|run|jump|swim|play|appear|exist|reside|live)\b/i.test(rest);
    if (startsWithBareVerb) {
      rest = `that ${rest}`;
    }
    // Final stranded-preposition guard — the auxiliary-rewriting above can
    // leave a bare preposition at the end ("countries Spain have a land
    // border with"). Drop the clue rather than ship it.
    if (endsWithStrandedPrep(rest.trim())) return null;
    return `This is the number of ${rest}.`;
  }
  // "How long" / "How old" / "How tall" — same family.
  m = body.match(/^How\s+(long|old|tall|deep|high|fast|big)\s+(.+)$/i);
  if (m) {
    const adj = m[1]!.toLowerCase();
    let rest = m[2]!;
    // Strip leading aux ("is" / "was" / "did" / "does") — "How long was X" →
    // "This is how long X was." reads better than "This is how long was X."
    const auxMatch = rest.match(/^(is|are|was|were|did|do|does)\s+(.+)$/i);
    if (auxMatch) {
      const aux = auxMatch[1]!.toLowerCase();
      rest = `${auxMatch[2]!} ${aux}`;
    }
    return `This is how ${adj} ${rest}.`;
  }

  // Rule 8: prompt has a blank ("___" or "_____") — treat as fill-in.
  if (/_{2,}/.test(body)) {
    const filled = body.replace(/_{2,}/g, '[this]');
    return `${capitalize(filled)}.`;
  }

  // Rule 9: prompt ends with "...":
  if (/\.{2,}$/.test(prompt)) {
    return `${prompt.replace(/\.{2,}$/, '').trim()}: this.`;
  }

  // No good rule.
  return null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return trimmed;
  return trimmed[0]!.toUpperCase() + trimmed.slice(1);
}

/**
 * Conservative past-tense conjugation for the bare-infinitive tail of
 * "When did X <verb>" prompts. Returns null when we don't have a confident
 * rule — the caller drops the clue in that case.
 *
 * We handle:
 *  - A small irregular-verb table (the verbs that actually appear in our
 *    OpenTDB corpus).
 *  - Regular "-ed" suffixation, with the small spelling adjustments needed
 *    for that suffix to read correctly ("publish" → "published",
 *    "release" → "released", "land" → "landed", "stop" → "stopped").
 *
 * We refuse anything else (length < 3, mixed case, etc.) — a wrong
 * conjugation reads worse than a dropped clue.
 */
const IRREGULAR_PAST: Record<string, string> = {
  arise: 'arose',
  awake: 'awoke',
  become: 'became',
  begin: 'began',
  bend: 'bent',
  bite: 'bit',
  blow: 'blew',
  break: 'broke',
  bring: 'brought',
  build: 'built',
  buy: 'bought',
  catch: 'caught',
  choose: 'chose',
  come: 'came',
  cost: 'cost',
  cut: 'cut',
  deal: 'dealt',
  dig: 'dug',
  do: 'did',
  draw: 'drew',
  drink: 'drank',
  drive: 'drove',
  eat: 'ate',
  end: 'ended',
  fall: 'fell',
  feed: 'fed',
  feel: 'felt',
  fight: 'fought',
  find: 'found',
  fly: 'flew',
  forget: 'forgot',
  freeze: 'froze',
  get: 'got',
  give: 'gave',
  go: 'went',
  grow: 'grew',
  hang: 'hung',
  have: 'had',
  hear: 'heard',
  hide: 'hid',
  hit: 'hit',
  hold: 'held',
  hurt: 'hurt',
  keep: 'kept',
  know: 'knew',
  lay: 'laid',
  lead: 'led',
  leave: 'left',
  lend: 'lent',
  let: 'let',
  lie: 'lay',
  light: 'lit',
  lose: 'lost',
  make: 'made',
  mean: 'meant',
  meet: 'met',
  pay: 'paid',
  put: 'put',
  read: 'read',
  ride: 'rode',
  ring: 'rang',
  rise: 'rose',
  run: 'ran',
  say: 'said',
  see: 'saw',
  sell: 'sold',
  send: 'sent',
  set: 'set',
  shake: 'shook',
  shine: 'shone',
  shoot: 'shot',
  show: 'showed',
  shut: 'shut',
  sing: 'sang',
  sink: 'sank',
  sit: 'sat',
  sleep: 'slept',
  speak: 'spoke',
  spend: 'spent',
  stand: 'stood',
  steal: 'stole',
  stick: 'stuck',
  strike: 'struck',
  swear: 'swore',
  sweep: 'swept',
  swim: 'swam',
  swing: 'swung',
  take: 'took',
  teach: 'taught',
  tear: 'tore',
  tell: 'told',
  think: 'thought',
  throw: 'threw',
  understand: 'understood',
  wake: 'woke',
  wear: 'wore',
  win: 'won',
  write: 'wrote',
};

function pastTenseFor(verb: string): string | null {
  const lower = verb.toLowerCase().replace(/[?.!,]+$/, '');
  if (lower.length < 2) return null;
  // Reject anything with punctuation, parens, digits or mixed-case (proper
  // nouns, abbreviations) — those aren't verbs.
  if (!/^[a-z]+$/.test(lower)) return null;
  if (IRREGULAR_PAST[lower]) return IRREGULAR_PAST[lower];
  // Already ends in "ed" — return as-is.
  if (lower.endsWith('ed')) return lower;
  // Common -e ending → just add d.
  if (lower.endsWith('e')) return lower + 'd';
  // Consonant + y → ied.
  if (lower.endsWith('y') && lower.length >= 3 && !/[aeiou]y$/.test(lower)) {
    return lower.slice(0, -1) + 'ied';
  }
  // CVC short verbs double the final consonant before -ed: "stop" → "stopped".
  // We only trip this rule on a short whitelist of common forms to avoid
  // mistakes ("publish" should NOT become "publishhed").
  if (
    /^[bcdfghjklmnpqrstvwxz][aeiou][bdgmnprt]$/.test(lower) &&
    !/[wxy]$/.test(lower)
  ) {
    return lower + lower[lower.length - 1] + 'ed';
  }
  // Plain "-ed" suffix.
  return lower + 'ed';
}

/**
 * Markers that indicate the answer is a company / team / organization, NOT a
 * person, even when the rest of the string looks name-like ("Real Madrid
 * C.F.", "Sony Inc.", "Manchester United F.C.", "Lego A/S").
 *
 * Match is case-insensitive on the whole word, with optional trailing period.
 */
const ORG_SUFFIXES = [
  'inc',
  'corp',
  'corporation',
  'co',
  'company',
  'ltd',
  'limited',
  'llc',
  'plc',
  'gmbh',
  'ag',
  'sa',
  's.a',
  's.a.',
  'cf',
  'c.f',
  'c.f.',
  'fc',
  'f.c',
  'f.c.',
  'sc',
  's.c',
  's.c.',
  'bv',
  'b.v',
  'b.v.',
  'a/s',
  'oy',
  'k.k',
  'k.k.',
];

/** True when the answer ends with an organization-suffix marker. */
function looksLikeOrganization(answer: string): boolean {
  // Strip trailing punctuation before isolating the last token.
  const cleaned = answer.trim().replace(/[?.!,]+$/, '');
  const tokens = cleaned.split(/\s+/);
  if (tokens.length === 0) return false;
  // Normalize the last token: lowercase + drop the trailing period if any.
  const last = tokens[tokens.length - 1]!.toLowerCase();
  if (ORG_SUFFIXES.includes(last)) return true;
  // Some suffixes appear without a period at the end of the answer; also check
  // the bare-period-stripped form.
  const bare = last.replace(/\.+$/, '');
  if (ORG_SUFFIXES.includes(bare)) return true;
  return false;
}

/** Honorifics / titles that unambiguously mark a person. */
const PERSON_TITLE_RE =
  /\b(Mr|Mrs|Ms|Sir|Lady|Queen|King|President|Pope|Saint|Dr|Lord|Duke|Duchess|Prince|Princess|Emperor|Empress|Tsar|Czar|Captain|General|Colonel)\b\.?/;

/** Common English words that, when capitalized in a 2-3 word answer, suggest
 * it's a place / nickname / landmark / breed / object — NOT a person. */
const NON_PERSON_WORD_RE =
  /\b(Great|Royal|Golden|Silver|White|Black|Red|Blue|Green|Yellow|Big|Little|Old|New|North|South|East|West|Saint|Mount|Mt|Cape|Lake|River|Port|Fort|Lord|Lady|City|Bay|Sea|Ocean|Island|Memorial|Tower|Hall|Bridge|Square|Center|Centre|Palace|Park|Plaza|Building|Station|Express|Avenue|Street|Road|Goose|Eagle|Hawk|Lion|Tiger|Bear|Wolf|Dragon|Knight|Queen|King)\b/;

/** Particles that appear inside names (lower case): "de la Cruz", "von Braun". */
const NAME_PARTICLES = new Set([
  'de',
  'da',
  'von',
  'van',
  'le',
  'la',
  'del',
  'di',
  'der',
  'den',
  'des',
  'du',
  'el',
]);

/**
 * Heuristic: does this answer look like a person?
 *
 * Bias toward `What is` (the safer default for things, places, teams, objects)
 * and only return true when there's a clear personal-name signal.
 *
 * Positive signals (any one):
 *  - Honorific / title prefix or suffix ("Mr.", "Queen", "Sir")
 *  - Middle-initial pattern ("J. R. R. Tolkien", "Hubert J. Farnsworth")
 *  - Name particle in the middle ("Leonardo da Vinci", "Ferdinand de Lesseps",
 *    "Wernher von Braun")
 *
 * Negative signals (any one disqualifies):
 *  - Organization suffix (`Inc.`, `Corp.`, `F.C.`, ...)
 *  - Contains a digit
 *  - Contains a common place/object word (`Mountain`, `River`, `Memorial`,
 *    `Goose`, `Eagle`, ...)
 *
 * Otherwise: default to NOT a person — we'd rather under-call "Who is" than
 * read "Who is Spruce Goose?" on the reveal card.
 */
function looksLikePerson(answer: string): boolean {
  // Organizations always win.
  if (looksLikeOrganization(answer)) return false;
  // Digits → not a person.
  if (/\d/.test(answer)) return false;
  // Common place / object words → not a person.
  if (NON_PERSON_WORD_RE.test(answer)) {
    // Exception: honorific override — "Queen Elizabeth II" should still be a
    // person even though "Queen" is in the negative list. But the override
    // is too eager when a place-word *follows* the title ("Prince Edward
    // Island" reads as a place, not a person). Require:
    //   - A title (Queen / Prince / Sir / etc.) at the start AND
    //   - No place-word token AFTER the title's match.
    // The first-token check keeps "Queen Elizabeth II" working while
    // dropping "Prince Edward Island".
    const titleMatch = answer.match(PERSON_TITLE_RE);
    if (titleMatch && titleMatch.index !== undefined) {
      const afterTitle = answer.slice(titleMatch.index + titleMatch[0].length);
      if (!NON_PERSON_WORD_RE.test(afterTitle)) return true;
    }
    return false;
  }
  // Strong positive: honorific / title.
  if (PERSON_TITLE_RE.test(answer)) return true;

  const words = answer.trim().split(/\s+/);
  if (words.length < 2 || words.length > 5) return false;
  // All tokens must be capitalized words, name particles, or middle-initials.
  const allNameLike = words.every(
    (w) =>
      /^[A-Z][a-zA-Z'-]*$/.test(w) || // capitalized word
      /^[A-Z]\.$/.test(w) || // single-letter initial like "J."
      NAME_PARTICLES.has(w.toLowerCase()),
  );
  if (!allNameLike) return false;

  // Strong positive: contains a name particle ("Leonardo da Vinci",
  // "Wernher von Braun", "Ferdinand de Lesseps").
  if (words.some((w) => NAME_PARTICLES.has(w.toLowerCase()))) return true;

  // Strong positive: contains a middle initial.
  if (words.some((w) => /^[A-Z]\.$/.test(w))) return true;

  // Relaxed positive (v1.2): 2-3 capitalized tokens where every token has 3+
  // letters classifies as a person. This catches "Sacha Baron Cohen", "Daniel
  // Ek", "Ole Kirk Christiansen", etc. The negative signals above (digits,
  // org suffix, place words) have already filtered out things, places, and
  // teams, so the false-positive risk is low.
  if (words.length >= 2 && words.length <= 3) {
    const allMeaty = words.every((w) => w.length >= 3);
    if (allMeaty) return true;
  }

  return false;
}

/**
 * Acceptable alternates for matching — strip parens, common suffixes, etc.
 * Pulled from a small set of conventions found in the OpenTDB corpus.
 */
function alternatesFor(answer: string): string[] {
  const alts = new Set<string>();
  alts.add(answer);
  // Strip parenthetical content: "Paris (France)" → "Paris"
  const noParens = answer.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  if (noParens && noParens !== answer) alts.add(noParens);
  // Strip "The " prefix.
  const noThe = answer.replace(/^The\s+/i, '').trim();
  if (noThe && noThe !== answer) alts.add(noThe);
  // For person names, add a last-name-only alternate when the answer is 2-3
  // capitalized words ("Aretha Franklin" → "Franklin"). Only do this if the
  // last word is >= 4 characters (avoid initials).
  const words = answer.trim().split(/\s+/);
  if (words.length >= 2 && words.length <= 3 && looksLikePerson(answer)) {
    const last = words[words.length - 1];
    if (last && last.length >= 4) {
      alts.add(last);
    }
  }
  // For numeric answers, accept written-out forms for small numbers? Not
  // worth the complexity in v1.
  alts.delete(answer); // canonical is matched separately
  return Array.from(alts);
}

/**
 * Prompts that can't be Jeopardy-fied gracefully. Skipped at pool-build time.
 */
function isHardReject(prompt: string): boolean {
  // "Which of these is NOT a ..." — answer is the odd one out, doesn't read
  // as a clue.
  if (/which\s+of\s+(these|the\s+following)\s+is\s+not/i.test(prompt)) return true;
  if (/which\s+of\s+(these|the\s+following)\s+(does\s+not|are\s+not)/i.test(prompt)) {
    return true;
  }
  // "All of the following EXCEPT" — same pattern.
  if (/all\s+of\s+the\s+following\s+except/i.test(prompt)) return true;
  // "True or False" — doesn't translate.
  if (/^(true|false)\s+or\s+(true|false)\b/i.test(prompt)) return true;
  if (/which\s+statement\s+is/i.test(prompt)) return true;
  // Multiple-choice-only forms ("Which of the following ..."): keep some,
  // strip the "of the following" so the rewrite is clean.
  // We don't reject — the rewriter will handle it.
  return false;
}
